<script lang="ts" setup>
  import {
    type Component,
    type HTMLAttributes,
    computed,
    defineAsyncComponent,
    shallowRef,
    useAttrs,
    watch,
  } from 'vue';
  import { Tooltip as vTooltip } from 'vuetify/directives/tooltip';

  import { toSystemColor } from '@bv/utils/common/color/to-system-color';
  import { trans } from '@bv/utils/i18n';

  import { resolveBvIconImport, warnUnknownBvIcon } from './partials/loaders';

  import type { BvIconValue } from './partials/bv-icon-types.generated';

  const FALLBACK_ICON: BvIconValue = 'bvi-close';

  defineOptions({
    inheritAttrs: false,
  });

  const attrs = useAttrs();

  export interface Props {
    /** Local icon as `bvi-<name>`, `mdi-*` webfont (needs `@mdi/font` CSS), or a Vue `Component`. */
    icon: BvIconValue;
    /** Pixel size, or preset: `sm` 16 (default), `md` 20, `lg` 24, `xl` 32. */
    size?: number | 'sm' | 'md' | 'lg' | 'xl';
    /** CSS color applied to the wrapper (SVGs should use `currentColor`). */
    color?: string;
    /** CSS `opacity` (number 0–1 or string e.g. `0.5`, `50%`). */
    opacity?: number | string;
    /** When set with `title` or alone, the icon is exposed to assistive tech (not `aria-hidden`). */
    ariaLabel?: string;
    title?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    size: 'sm',
    color: undefined,
    opacity: undefined,
    ariaLabel: undefined,
    title: undefined,
  });

  const tooltipProps = computed(() => {
    return {
      text: props.title ? trans(props.title) : undefined,
      location: 'top center',
    };
  });

  const isMdiWebfontName = (icon: string): boolean => {
    const name = icon.trim().toLowerCase();
    return name.startsWith('mdi-') && name.length > 4;
  };

  const sizePx = computed<number>(() => {
    if (typeof props.size === 'number') {
      return props.size;
    }
    const map = { sm: 16, md: 20, lg: 24, xl: 32 };
    return map[props.size] ?? 16;
  });

  const boxStyle = computed<Record<string, string>>(() => {
    const style: Record<string, string> = {
      width: `${sizePx.value}px`,
      height: `${sizePx.value}px`,
    };
    if (props.color !== undefined && props.color !== '') {
      style.color = toSystemColor(props.color);
    }
    if (props.opacity !== undefined && props.opacity !== '') {
      style.opacity = typeof props.opacity === 'number' ? String(props.opacity) : props.opacity;
    }
    return style;
  });

  const rootStyle = computed(() => {
    const incoming = attrs.style;
    if (Array.isArray(incoming)) {
      return [boxStyle.value, ...incoming];
    }
    if (incoming && typeof incoming === 'object') {
      return { ...boxStyle.value, ...(incoming as Record<string, string>) };
    }
    if (typeof incoming === 'string') {
      return [boxStyle.value, incoming];
    }
    return boxStyle.value;
  });

  const passthroughAttrs = computed<Record<string, unknown>>(() => {
    const { class: _c, style: _s, ...rest } = attrs as Record<string, unknown>;
    return rest;
  });

  const rootClass = computed<HTMLAttributes['class']>(() => attrs.class as HTMLAttributes['class']);

  const isDecorative = computed<boolean>(() => !props.ariaLabel && !props.title);

  const isMdi = computed<boolean>(() => typeof props.icon === 'string' && isMdiWebfontName(props.icon));

  const mdiIconClass = computed<string>(() => (typeof props.icon === 'string' ? props.icon.trim().toLowerCase() : ''));

  const iconComponent = computed<Component | null>(() => (typeof props.icon !== 'string' ? props.icon : null));

  const mdiGlyphStyle = computed<Record<string, string>>(() => ({
    fontSize: `${sizePx.value}px`,
    lineHeight: '1',
  }));

  const IconAsync = shallowRef<Component | null>(null);

  watch(
    () => props.icon,
    (icon) => {
      if (typeof icon !== 'string') {
        IconAsync.value = null;
        return;
      }
      if (isMdi.value) {
        IconAsync.value = null;
        return;
      }
      const primary = resolveBvIconImport(icon);
      const loader = primary ?? resolveBvIconImport(FALLBACK_ICON);
      if (!primary) {
        warnUnknownBvIcon(icon);
      }
      if (!loader) {
        IconAsync.value = null;
        return;
      }
      IconAsync.value = defineAsyncComponent(loader);
    },
    { immediate: true },
  );
</script>

<template>
  <span
    v-tooltip="tooltipProps"
    v-bind="passthroughAttrs"
    :aria-hidden="isDecorative ?? undefined"
    :aria-label
    class="bv-icon inline-flex shrink-0 items-center justify-center"
    :class="rootClass"
    :style="rootStyle"
  >
    <i
      v-if="isMdi"
      class="mdi leading-none"
      :class="mdiIconClass"
      role="presentation"
      :style="mdiGlyphStyle"
    />
    <component
      :is="IconAsync || iconComponent"
      v-else-if="IconAsync || iconComponent"
      class="block h-full w-full [&_svg]:block [&_svg]:h-full [&_svg]:w-full"
    />
  </span>
</template>
